import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const CalendarSettings = () => {
  const [bookingUrl, setBookingUrl] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  
  useEffect(() => {
    // Try to get company ID from various sources
    const fetchCompanyId = async () => {
      const debug = {};
      
      // Try from localStorage.user
      const userJson = localStorage.getItem('user');
      debug.userJson = userJson ? 'present' : 'missing';
      
      if (userJson) {
        try {
          const userData = JSON.parse(userJson);
          debug.parsedUser = userData ? 'parsed' : 'parse failed';
          debug.userCompanyId = userData.company_id || 'no company_id';
          
          if (userData && userData.company_id) {
            setCompanyId(userData.company_id);
            return;
          }
        } catch (e) {
          debug.parseError = e.message;
        }
      }
      
      // Try from API
      try {
        const token = localStorage.getItem('token');
        debug.token = token ? 'present' : 'missing';
        
        if (token) {
          const response = await fetch('/api/auth/me', {
            headers: {
              'x-auth-token': token
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            debug.apiUser = userData ? 'received' : 'empty';
            debug.apiCompanyId = userData?.company_id || 'no company_id';
            
            if (userData && userData.company_id) {
              setCompanyId(userData.company_id);
              return;
            }
          } else {
            debug.apiError = `Status: ${response.status}`;
          }
        }
      } catch (error) {
        debug.fetchError = error.message;
      }
      
      // Last resort: hardcode for testing on localhost
      if (window.location.hostname === 'localhost') {
        debug.usingHardcoded = true;
        setCompanyId(1);
        return;
      }
      
      setDebugInfo(debug);
    };
    
    fetchCompanyId();
  }, []);
  
  useEffect(() => {
    // Generate booking URL using the current domain and company ID
    const domain = window.location.origin;
    setBookingUrl(`${domain}/booking.html?company=${companyId || ''}`);
  }, [companyId]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(bookingUrl)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
        toast.success('Booking URL copied to clipboard!');
      })
      .catch((error) => {
        console.error('Failed to copy URL:', error);
        toast.error('Failed to copy URL to clipboard');
      });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">Public Booking</h2>
      
      <div className="space-y-6">
        <div>
          <p className="mb-2 text-gray-700">
            You can allow students to book classes directly from your website by using the booking link below.
            To make a class available for public booking, edit the class and check the "Allow public booking" option.
          </p>
          
          <div className="mt-4 bg-gray-100 p-4 rounded-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Public Booking URL</label>
            <div className="flex">
              <input
                type="text"
                readOnly
                value={bookingUrl}
                className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md bg-white text-gray-700"
              />
              <button
                onClick={copyToClipboard}
                className={`px-4 py-2 rounded-r-md ${
                  isCopied ? 'bg-green-500' : 'bg-blue-600'
                } text-white focus:outline-none`}
              >
                {isCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            
            {/* Display debug info (only visible during development/testing) */}
            {Object.keys(debugInfo).length > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs font-mono">
                <div className="font-bold mb-1">Debug Info:</div>
                <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                <div className="mt-2 text-xs text-gray-500">
                  Using company ID: {companyId || 'none'}
                </div>
              </div>
            )}
          </div>
          
          <p className="mt-4 text-sm text-gray-500">
            You can embed this URL in your website or share it directly with potential students.
            Only classes marked as "public" will be visible on this page.
          </p>
        </div>
        
        <div className="border-t pt-4">
          <h3 className="font-medium text-lg mb-2">How it works</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Share your booking URL with potential students</li>
            <li>Students can see all your publicly available classes</li>
            <li>When a student books a class, a lead is automatically created in your CRM</li>
            <li>You receive a notification of the new booking</li>
            <li>You can track all bookings in the calendar view</li>
          </ol>
        </div>
        
        <div className="border-t pt-4">
          <h3 className="font-medium text-lg mb-2">Customization</h3>
          <p className="text-gray-700">
            You can customize the appearance of your booking page by updating your company profile 
            information and logo in the Company Settings tab.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CalendarSettings;